import { useEffect, useState } from "react";
import { toast } from "sonner";
import { format } from "date-fns";
import { Plus, Loader2, Trash2, Copy, Eye, Clock, Download, Presentation, Power, CalendarIcon, BarChart3, Users, UserPlus, Bell, ThumbsDown, Stethoscope, Sparkles } from "lucide-react";
import { AppShell } from "@/components/app/AppShell";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { formatDate } from "@/lib/format";

const EMPTY = { doc_id: "", name: "", require_email: true, passcode: "", allow_download: false, expires_at: "", watermark: false, watermark_opacity: 0.16, watermark_timestamp: false };

const fmtTime = (secs) => {
  if (!secs) return "0s";
  if (secs < 60) return `${secs}s`;
  return `${Math.floor(secs / 60)}m ${secs % 60}s`;
};

export default function DeckLinks() {
  const { company } = useAuth();
  const [links, setLinks] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [sdBusy, setSdBusy] = useState(false);

  const runSlideDoctor = async () => {
    setSdBusy(true);
    try {
      const { data } = await api.post(`/doc-links/${analytics.link.id}/slide-doctor`);
      setAnalytics((a) => ({ ...a, link: { ...a.link, slide_doctor: data } }));
      toast.success("Slide Doctor finished");
    } catch (e) { toast.error(e?.response?.data?.detail || "Slide Doctor failed"); }
    finally { setSdBusy(false); }
  };
  const [added, setAdded] = useState({});

  const load = async () => {
    if (!company) return;
    try {
      const [l, d] = await Promise.all([
        api.get(`/companies/${company.id}/doc-links`),
        api.get(`/companies/${company.id}/documents`),
      ]);
      setLinks(l.data);
      setDocuments(d.data);
    } catch (e) { console.error("deck links load failed", e); setLinks([]); }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company]);

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post(`/companies/${company.id}/doc-links`, form);
      const url = `${window.location.origin}/api/sd/${data.token}`;
      try { await navigator.clipboard.writeText(url); toast.success("Tracked link created and copied"); }
      catch { toast.success(`Link created: ${url}`); }
      setDialog(false); setForm(EMPTY);
      load();
    } catch (err) { if (err?.response?.status !== 402) toast.error(err?.response?.data?.detail || "Could not create link"); }
    finally { setSaving(false); }
  };

  const copy = async (l) => {
    const url = `${window.location.origin}/api/sd/${l.token}`;
    try { await navigator.clipboard.writeText(url); toast.success("Link copied"); }
    catch { toast.success(url); }
  };

  const toggle = async (l) => {
    try { await api.patch(`/doc-links/${l.id}`, { active: !l.active }); load(); }
    catch { toast.error("Could not update link"); }
  };

  const remove = async (l) => {
    try { await api.delete(`/doc-links/${l.id}`); toast.success("Link deleted"); if (analytics?.link?.id === l.id) setAnalytics(null); load(); }
    catch { toast.error("Delete failed"); }
  };

  const openAnalytics = async (l) => {
    try { const { data } = await api.get(`/doc-links/${l.id}/analytics`); setAnalytics(data); }
    catch { toast.error("Could not load analytics"); }
  };

  const addToPipeline = async (v) => {
    try {
      await api.post(`/companies/${company.id}/deck-leads/prospect`, { email: v.email, link_id: analytics?.link?.id });
      setAdded((m) => ({ ...m, [v.email]: true }));
      toast.success(`${v.email} added to your fundraising pipeline`);
    } catch (e) {
      if (e?.response?.status === 409) {
        setAdded((m) => ({ ...m, [v.email]: true }));
        toast.info("This viewer is already in your pipeline");
      } else if (e?.response?.status !== 402) {
        toast.error(e?.response?.data?.detail || "Could not add to pipeline");
      }
    }
  };

  const totalLeads = (links || []).reduce((n, l) => n + (l.leads || 0), 0);
  const exportLeads = async () => {
    try {
      const res = await api.get(`/companies/${company.id}/deck-leads.csv`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(company.name || "company").replace(/\s+/g, "_").toLowerCase()}_deck_leads.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Leads exported");
    } catch { toast.error("Could not export leads"); }
  };

  return (
    <AppShell title="Deck Links" subtitle="DocSend-style tracked links for your pitch deck and documents"
      actions={
        <div className="flex items-center gap-2">
          {totalLeads > 0 && (
            <Button variant="outline" className="gap-2" onClick={exportLeads} data-testid="export-leads-btn">
              <Download className="h-4 w-4" /> Export leads ({totalLeads})
            </Button>
          )}
          <Button className="gap-2" onClick={() => setDialog(true)} data-testid="link-create-btn"><Plus className="h-4 w-4" /> New tracked link</Button>
        </div>
      }>
      {links === null ? <p className="text-sm text-muted-foreground">Loading…</p> : links.length === 0 ? (
        <Card className="rounded-xl border border-dashed p-10 text-center">
          <Presentation className="mx-auto h-8 w-8 text-muted-foreground" />
          <h2 className="mt-3 text-sm font-semibold text-foreground">No tracked links yet</h2>
          <p className="mx-auto mt-1 max-w-sm text-sm text-muted-foreground">Share your deck with an email-gated link and see exactly who opened it, when and for how long.</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {links.map((l) => (
            <Card key={l.id} className="rounded-xl border bg-card p-4" data-testid={`link-row-${l.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-foreground">{l.name}</span>
                    {!l.active && <Badge className="border-red-200 bg-red-50 text-red-600">Off</Badge>}
                    {l.expired && <Badge className="border-amber-200 bg-amber-50 text-amber-800">Expired</Badge>}
                    {l.passcode && <Badge variant="secondary" className="font-normal">Passcode</Badge>}
                    {!l.allow_download && <Badge variant="secondary" className="font-normal">View only</Badge>}
                  </div>
                  <div className="mt-0.5 text-xs text-muted-foreground">{l.doc_name}{l.expires_at ? ` · expires ${l.expires_at}` : ""}</div>
                </div>
                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1"><Eye className="h-3.5 w-3.5" /> {l.visits} visit{l.visits === 1 ? "" : "s"}</span>
                  <span className="flex items-center gap-1" data-testid={`link-leads-${l.id}`}><Users className="h-3.5 w-3.5" /> {l.leads || 0} lead{l.leads === 1 ? "" : "s"}</span>
                        {(l.interested_count || 0) > 0 && <span className="flex items-center gap-1 font-semibold text-emerald-700" data-testid={`link-interested-${l.id}`}><Bell className="h-3.5 w-3.5" /> {l.interested_count} interested</span>}
                        {(l.pass_count || 0) > 0 && <span className="flex items-center gap-1 text-muted-foreground" data-testid={`link-passes-${l.id}`}><ThumbsDown className="h-3.5 w-3.5" /> {l.pass_count} pass{l.pass_count === 1 ? "" : "es"}</span>}
                  <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {fmtTime(l.total_seconds)}</span>
                  <div className="flex items-center gap-1.5">
                    <Button variant="outline" size="sm" className="gap-1.5" onClick={() => copy(l)} data-testid={`link-copy-${l.id}`}><Copy className="h-3.5 w-3.5" /> Copy</Button>
                    <Button variant="outline" size="sm" className="gap-1.5" onClick={() => openAnalytics(l)} data-testid={`link-analytics-${l.id}`}><Eye className="h-3.5 w-3.5" /> Analytics</Button>
                    <Button variant="ghost" size="icon" className={`h-8 w-8 ${l.active ? "text-emerald-600" : "text-muted-foreground"}`} onClick={() => toggle(l)} title={l.active ? "Deactivate" : "Activate"} data-testid={`link-toggle-${l.id}`}><Power className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-red-600" onClick={() => remove(l)} data-testid={`link-delete-${l.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!analytics} onOpenChange={(v) => !v && setAnalytics(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{analytics?.link?.name}</DialogTitle>
            <DialogDescription>Who viewed this link, when and for how long</DialogDescription>
          </DialogHeader>
          <div className="max-h-[50vh] space-y-2 overflow-y-auto" data-testid="link-visits">
              <div className="mb-3 rounded-lg border border-brand/30 bg-brand/[0.04] p-3" data-testid="slide-doctor">
              <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1.5 text-xs font-semibold text-brand-ink"><Stethoscope className="h-3.5 w-3.5" /> Slide Doctor</div>
                  <Button size="sm" variant="outline" className="h-6 gap-1 border-brand/40 px-2 text-[11px]" disabled={sdBusy} onClick={runSlideDoctor} data-testid="slide-doctor-run">
                  {sdBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
                  {sdBusy ? "Diagnosing…" : analytics?.link?.slide_doctor ? "Re-run" : "Diagnose drop-offs"}
                </Button>
              </div>
              {analytics?.link?.slide_doctor ? (
                <div className="mt-2 space-y-2" data-testid="slide-doctor-result">
                  <p className="text-[11px] font-medium text-foreground">{analytics.link.slide_doctor.overall}</p>
                  {(analytics.link.slide_doctor.slides || []).map((s) => (
                    <div key={s.page} className="rounded-md border bg-card p-2" data-testid={`slide-doctor-page-${s.page}`}>
                        <div className="text-[10px] font-bold uppercase tracking-wide text-brand-ink">Slide {s.page}</div>
                      <p className="mt-0.5 text-[11px] text-muted-foreground"><span className="font-semibold text-foreground">Why:</span> {s.diagnosis}</p>
                      <p className="mt-0.5 text-[11px] text-muted-foreground"><span className="font-semibold text-foreground">Fix:</span> {s.fix}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="mt-1 text-[11px] text-muted-foreground">AI reads your per-slide drop-off and tells you why viewers bail on your worst slides, and how to fix them.</p>
              )}
            </div>
            {(analytics?.responses || []).length > 0 && (
              <div className="mb-3 rounded-lg border bg-secondary/30 p-3" data-testid="link-responses">
                <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-foreground"><Bell className="h-3.5 w-3.5 text-primary" /> Viewer responses</div>
                <div className="space-y-1.5">
                  {analytics.responses.map((r) => (
                    <div key={r.id} className="flex items-center justify-between gap-2 text-[11px]" data-testid={`response-row-${r.id}`}>
                      <span className="truncate font-medium text-foreground">{r.email || "Anonymous"}</span>
                      <span className={`shrink-0 rounded-full px-2 py-0.5 font-semibold ${r.response === "interested" ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-600"}`}>
                        {r.response === "interested" ? "keep me posted" : `pass${r.reason ? ` - ${r.reason}` : ""}`}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {(analytics?.pages || []).length > 0 && (
              <div className="mb-3 rounded-lg border bg-secondary/30 p-3" data-testid="link-page-analytics">
                <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-foreground"><BarChart3 className="h-3.5 w-3.5 text-primary" /> Time per page (all viewers)</div>
                {(() => {
                  const maxSecs = Math.max(...analytics.pages.map((p) => p.seconds), 1);
                  return (
                    <div className="space-y-1">
                      {analytics.pages.map((p) => (
                        <div key={p.page} className="flex items-center gap-2 text-[11px]" data-testid={`page-bar-${p.page}`}>
                          <span className="w-12 shrink-0 text-muted-foreground">Pg {p.page}</span>
                          <div className="h-3 flex-1 overflow-hidden rounded bg-muted">
                            <div className="h-full rounded bg-primary transition-all" style={{ width: `${(p.seconds / maxSecs) * 100}%` }} />
                          </div>
                          <span className="w-12 shrink-0 text-right text-muted-foreground">{fmtTime(p.seconds)}</span>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
            )}
            {(analytics?.visits || []).length === 0 ? <p className="text-sm text-muted-foreground">No visits yet.</p> :
              analytics.visits.map((v) => (
                <div key={v.id} className="flex items-center justify-between rounded-lg border p-3 text-xs" data-testid={`visit-row-${v.id}`}>
                  <div>
                    <div className="font-semibold text-foreground">{v.email || "Anonymous viewer"}</div>
                    <div className="text-muted-foreground">{formatDate(v.started_at)}</div>
                  </div>
                  <div className="flex items-center gap-3 text-muted-foreground">
                    <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {fmtTime(v.seconds)}</span>
                    {v.downloaded && <span className="flex items-center gap-1 text-primary"><Download className="h-3 w-3" /> downloaded</span>}
                    {v.email && (
                      <Button variant="outline" size="sm" className="h-7 gap-1 px-2 text-[11px]" disabled={!!added[v.email]}
                        onClick={() => addToPipeline(v)} data-testid={`visit-add-prospect-${v.id}`}>
                        <UserPlus className="h-3 w-3" /> {added[v.email] ? "In pipeline" : "Add to pipeline"}
                      </Button>
                    )}
                  </div>
                </div>
              ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>New tracked link</DialogTitle>
            <DialogDescription>Email-gated, trackable and revocable at any time</DialogDescription>
          </DialogHeader>
          <form onSubmit={create} className="space-y-3">
            <div>
              <Label>Document</Label>
              <Select value={form.doc_id} onValueChange={(v) => setForm((f) => ({ ...f, doc_id: v }))}>
                <SelectTrigger className="mt-1" data-testid="link-form-doc"><SelectValue placeholder="Pick from your vault" /></SelectTrigger>
                <SelectContent>{documents.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Link name</Label><Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Seed deck for Meridian" className="mt-1" data-testid="link-form-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Passcode (optional)</Label><Input value={form.passcode} onChange={(e) => setForm((f) => ({ ...f, passcode: e.target.value }))} className="mt-1" data-testid="link-form-passcode" /></div>
              <div>
                <Label>Expires on</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button type="button" variant="outline" className="mt-1 w-full justify-start gap-2 font-normal" data-testid="link-form-expiry">
                      <CalendarIcon className="h-4 w-4 text-muted-foreground" />
                      {form.expires_at ? format(new Date(form.expires_at + "T00:00:00"), "PP") : <span className="text-muted-foreground">No expiry</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar mode="single" selected={form.expires_at ? new Date(form.expires_at + "T00:00:00") : undefined}
                      onSelect={(d) => setForm((f) => ({ ...f, expires_at: d ? format(d, "yyyy-MM-dd") : "" }))}
                      disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))} initialFocus />
                    {form.expires_at && (
                      <div className="border-t p-2">
                        <Button type="button" variant="ghost" size="sm" className="w-full text-xs" onClick={() => setForm((f) => ({ ...f, expires_at: "" }))} data-testid="link-form-expiry-clear">Clear expiry</Button>
                      </div>
                    )}
                  </PopoverContent>
                </Popover>
              </div>
            </div>
            <div className="flex items-center justify-between rounded-lg border p-3">
              <span className="text-xs text-foreground">Require email to view</span>
              <Switch checked={form.require_email} onCheckedChange={(v) => setForm((f) => ({ ...f, require_email: v }))} data-testid="link-form-email" />
            </div>
            <div className="flex items-center justify-between rounded-lg border p-3">
              <span className="text-xs text-foreground">Allow download</span>
              <Switch checked={form.allow_download} onCheckedChange={(v) => setForm((f) => ({ ...f, allow_download: v }))} data-testid="link-form-download" />
            </div>
            <div className="flex items-center justify-between rounded-lg border p-3">
              <span className="text-xs text-foreground">Watermark each page with the viewer's email
                <span className="block text-[11px] text-muted-foreground">Burned into PDFs - survives download &amp; re-sharing</span>
              </span>
              <Switch checked={form.watermark} onCheckedChange={(v) => setForm((f) => ({ ...f, watermark: v }))} data-testid="link-form-watermark" />
            </div>
            {form.watermark && (
              <div className="space-y-2 rounded-lg border border-dashed p-3" data-testid="watermark-style">
                <div className="flex items-center justify-between gap-3">
                  <Label className="text-xs">Watermark strength</Label>
                  <Select value={String(form.watermark_opacity)} onValueChange={(v) => setForm((f) => ({ ...f, watermark_opacity: Number(v) }))}>
                    <SelectTrigger className="h-8 w-32 text-xs" data-testid="watermark-opacity"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0.08">Light</SelectItem>
                      <SelectItem value="0.16">Medium</SelectItem>
                      <SelectItem value="0.28">Strong</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-foreground">Include date &amp; time stamp</span>
                  <Switch checked={form.watermark_timestamp} onCheckedChange={(v) => setForm((f) => ({ ...f, watermark_timestamp: v }))} data-testid="watermark-timestamp" />
                </div>
              </div>
            )}
            <Button type="submit" disabled={saving || !form.doc_id} className="w-full gap-2" data-testid="link-form-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Create link
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
